import React from "react";
import { get } from "lodash-es";
import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { CONTRACT_INFO_STEP1_FIELD as FIELD } from "config/documentConstant";
import {
  ContractInfoBorderBox,
  ContractInfoSection,
  formatContractInfoNumber,
  formatContractInfoValue,
} from "./ContractInfoPdfLayout";

interface ContractInfoOverviewSectionProps {
  documentData: Record<string, unknown>;
}

const styles = StyleSheet.create({
  row: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "6mm",
    minHeight: "5.8mm",
    paddingTop: "0.6mm",
    paddingBottom: "0.6mm",
    borderBottom: "0.2mm solid #C6C8CD",
  },
  half: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  full: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  label: {
    width: "23.5mm",
    fontFamily: "Pretendard",
    fontSize: 9.46,
    lineHeight: 1.5,
    fontWeight: 700,
    color: "#171D23",
  },
  value: {
    fontFamily: "Pretendard",
    fontSize: 9.46,
    lineHeight: 1.5,
    fontWeight: 400,
    color: "#171D23",
  },
  valueWrap: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
});

const hasMeaningfulValue = (value: unknown) => {
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim();
  return normalized !== "" && normalized !== "-";
};

const PairField = ({ label, value }: { label: string; value: string }) => {
  return (
    <View style={styles.half}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.valueWrap}>
        <Text style={styles.value}>{value}</Text>
      </View>
    </View>
  );
};

const FullField = ({ label, value }: { label: string; value: string }) => {
  return (
    <View
      style={[
        styles.row,
        {
          minHeight: "5.8mm",
          alignItems: "center",
          borderBottom: "0mm solid transparent",
        },
      ]}
    >
      <View style={styles.full}>
        <Text style={styles.label}>{label}</Text>
        <View style={styles.valueWrap}>
          <Text style={styles.value}>{value}</Text>
        </View>
      </View>
    </View>
  );
};

const ContractInfoOverviewSection = ({
  documentData,
}: ContractInfoOverviewSectionProps) => {
  const landAreaInSquareMeters = get(
    documentData,
    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key,
  );
  const landAreaInPyeong = get(
    documentData,
    FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key,
  );

  const landAreaText = (() => {
    const squareMetersText = hasMeaningfulValue(landAreaInSquareMeters)
      ? `${formatContractInfoNumber(landAreaInSquareMeters)}㎡`
      : "";
    const pyeongText = hasMeaningfulValue(landAreaInPyeong)
      ? `${formatContractInfoNumber(landAreaInPyeong)}평`
      : "";

    if (squareMetersText && pyeongText) {
      return `${squareMetersText} (${pyeongText})`;
    }

    return squareMetersText || pyeongText || formatContractInfoValue("");
  })();

  const urbanManagementPlan = formatContractInfoValue(
    documentData[FIELD.URBAN_MANAGEMENT_PLAN.key],
  );
  const businessScale = formatContractInfoValue(
    documentData[FIELD.BUSINESS_SCALE.key],
  );
  const additionalNotes = formatContractInfoValue(
    documentData[FIELD.ADDITIONAL_NOTES.key],
  );

  const emptyText = formatContractInfoValue("");
  const showUrbanPlanRow =
    urbanManagementPlan !== emptyText || businessScale !== emptyText;
  const showAdditionalNotes = additionalNotes !== emptyText;

  return (
    <ContractInfoSection title="1. 사업 개요">
      <ContractInfoBorderBox>
        <View style={styles.row}>
          <PairField
            label="PJ명"
            value={formatContractInfoValue(documentData[FIELD.PJ.key])}
          />
          <PairField
            label="정보제공자"
            value={formatContractInfoValue(
              documentData[FIELD.INFORMATION_PROVIDER.key],
            )}
          />
        </View>

        <View style={styles.row}>
          <PairField
            label="접수일"
            value={formatContractInfoValue(
              documentData[FIELD.RECEIPT_DATE.key],
            )}
          />
          <PairField
            label="사업방식"
            value={formatContractInfoValue(
              documentData[FIELD.BUSINESS_METHOD.key],
            )}
          />
        </View>

        <View
          style={[
            styles.row,
            {
              borderBottom:
                showUrbanPlanRow || showAdditionalNotes
                  ? "0.2mm solid #C6C8CD"
                  : "0mm solid transparent",
            },
          ]}
        >
          <PairField
            label="시행사 및 관계사"
            value={formatContractInfoValue(
              documentData[FIELD.IMPLEMENTER_AND_RELATED_COMPANY.key],
            )}
          />
          <PairField label="토지면적" value={landAreaText} />
        </View>

        {showUrbanPlanRow && (
          <View
            style={[
              styles.row,
              {
                borderBottom: showAdditionalNotes
                  ? "0.2mm solid #C6C8CD"
                  : "0mm solid transparent",
              },
            ]}
          >
            <PairField label="도시관리 계획" value={urbanManagementPlan} />
            <PairField label="사업규모" value={businessScale} />
          </View>
        )}

        {showAdditionalNotes && (
          <FullField label="기타사항" value={additionalNotes} />
        )}
      </ContractInfoBorderBox>
    </ContractInfoSection>
  );
};

export default ContractInfoOverviewSection;
